import React from 'react'
import { FormControl, FormHelperText, MenuItem, Select, Typography } from '@mui/material'

export interface SettingsSelectOption {
    value: string
    label: string
}

interface SettingsSelectProps {
    value: string
    onChange: (val: string) => void
    options: SettingsSelectOption[]
    placeholder: string
    disabled?: boolean
    existingValue?: string
    error?: boolean
    helperText?: string
    // When true (and not already in an error state), show a red 'Required' hint beneath the select
    // without turning the box red — mirrors the required-field UX on the form's text inputs.
    required?: boolean
}

const SettingsSelect: React.FC<SettingsSelectProps> = ({
    value,
    onChange,
    options,
    placeholder,
    disabled,
    existingValue,
    error,
    helperText,
    required,
}) => {
    const isStale = Boolean(existingValue && existingValue !== '---' && !options.some((o) => o.value === existingValue))

    return (
        <FormControl fullWidth error={error}>
            <Select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                displayEmpty
                disabled={disabled}
                error={error}
                renderValue={(val) => {
                    if (!val || val === '---') {
                        return (
                            <Typography variant="16x600" component="span" sx={{ color: 'figma.primary' }}>
                                {placeholder}
                            </Typography>
                        )
                    }
                    if (isStale && val === existingValue) {
                        return (
                            <Typography variant="16x600" component="span">
                                {val}
                            </Typography>
                        )
                    }
                    return <span>{options.find((o) => o.value === val)?.label ?? val}</span>
                }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderRadius: '12px' } }}
            >
                <MenuItem value="---">---</MenuItem>
                {options.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
            {/* Always render, even with no error, so the field's height doesn't jump when one appears.
                A required-but-not-yet-errored field shows a red 'Required' hint; the box (driven by
                `error` on the FormControl) stays neutral until submit. */}
            <FormHelperText sx={required && !error ? { color: 'error.main' } : undefined}>
                {helperText || (required ? 'Required' : ' ')}
            </FormHelperText>
        </FormControl>
    )
}

export default SettingsSelect
